import { isNative } from './platform';

const KEY = 'hmawiw-token';

/** Where the device token lives: the Android Keystore in the app, localStorage on the web. */
export interface TokenStore {
  get(): Promise<string | null>;
  set(token: string): Promise<void>;
  clear(): Promise<void>;
}

const webTokenStore: TokenStore = {
  async get() {
    try {
      return localStorage.getItem(KEY);
    } catch {
      return null;
    }
  },
  async set(token) {
    try {
      localStorage.setItem(KEY, token);
    } catch {
      // storage unavailable: the session lasts until the page closes
    }
  },
  async clear() {
    try {
      localStorage.removeItem(KEY);
    } catch {
      // nothing to clear
    }
  },
};

const nativeTokenStore: TokenStore = {
  async get() {
    try {
      const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
      return (await SecureStoragePlugin.get({ key: KEY })).value || null;
    } catch {
      return null; // the plugin throws when the key is absent
    }
  },
  async set(token) {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
    await SecureStoragePlugin.set({ key: KEY, value: token });
  },
  async clear() {
    try {
      const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
      await SecureStoragePlugin.remove({ key: KEY });
    } catch {
      // already gone
    }
  },
};

let current: TokenStore = isNative ? nativeTokenStore : webTokenStore;

export function tokenStore(): TokenStore {
  return current;
}

export function useTokenStore(store: TokenStore): void {
  current = store;
}
